import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { LogIn, UserPlus, Mail, Lock, User } from 'lucide-react';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
  });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Logged in successfully!');
      } else {
        await register(formData.email, formData.password, formData.full_name);
        toast.success('Account created successfully!');
      }
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-32 relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="hero-glow hero-glow-1" />
        <div className="hero-glow hero-glow-2" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md relative z-20"
      >
        <div className="gradient-border rounded-3xl p-8 bg-gradient-to-br from-[#0a0e27]/90 to-[#1a1f3a]/90 backdrop-blur-2xl relative z-20">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-gradient-to-r from-primary/20 to-indigo-500/20 rounded-full border border-primary/30">
              {isLogin ? (
                <LogIn size={32} className="text-primary" />
              ) : (
                <UserPlus size={32} className="text-primary" />
              )}
            </div>
          </div>

          <h1 className="text-4xl font-heading font-bold uppercase tracking-tight mb-2 text-center bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="text-center text-gray-400 text-sm mb-8">
            {isLogin ? 'Sign in to access your dashboard' : 'Join us to get started'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5 relative z-30" data-testid="auth-form">
            {!isLogin && (
              <div>
                <label className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-2 block">
                  Full Name
                </label>
                <div className="relative">
                  <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                  <Input
                    name="full_name"
                    placeholder="John Doe"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    data-testid="auth-fullname-input"
                    className="bg-white/10 border-2 border-white/20 focus:border-primary focus:bg-white/15 transition-all text-white placeholder:text-gray-500 rounded-lg pl-12 pr-4 py-3 text-base w-full"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-2 block">
                Email Address
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                <Input
                  name="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="auth-email-input"
                  className="bg-white/10 border-2 border-white/20 focus:border-primary focus:bg-white/15 transition-all text-white placeholder:text-gray-500 rounded-lg pl-12 pr-4 py-3 text-base w-full"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-2 block">
                Password
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                <Input
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="auth-password-input"
                  className="bg-white/10 border-2 border-white/20 focus:border-primary focus:bg-white/15 transition-all text-white placeholder:text-gray-500 rounded-lg pl-12 pr-4 py-3 text-base w-full"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-button"
              className="w-full bg-gradient-to-r from-primary to-indigo-500 text-black hover:from-primary-hover hover:to-indigo-600 font-bold py-6 text-base uppercase tracking-wider shadow-xl shadow-primary/30 mt-6 relative z-40 cursor-pointer"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Please wait...
                </span>
              ) : isLogin ? (
                'Sign In'
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <div className="mt-8 text-center relative z-30">
            <button
              onClick={() => setIsLogin(!isLogin)}
              data-testid="toggle-auth-mode"
              className="text-sm text-gray-400 hover:text-primary transition-colors font-medium cursor-pointer"
            >
              {isLogin ? (
                <>
                  Don't have an account?{' '}
                  <span className="text-primary font-semibold">Create one</span>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <span className="text-primary font-semibold">Sign in</span>
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
